import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
  DEFAULT_DENIED,
  type ConsentCategory,
  type ConsentState,
} from './types';

interface ConsentContextValue {
  state: ConsentState | null;
  hasDecided: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  setCategory: (category: Exclude<ConsentCategory, 'necessary'>, granted: boolean) => void;
  save: (partial: Partial<Pick<ConsentState, 'analytics' | 'marketing'>>) => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

function readStored(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(state: ConsentState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage quota / disabled — fall back to in-memory only.
  }
}

interface ConsentProviderProps {
  children: React.ReactNode;
}

export function ConsentProvider({ children }: ConsentProviderProps) {
  const [state, setState] = useState<ConsentState | null>(() => readStored());
  const [isOpen, setIsOpen] = useState(false);

  const hasDecided = state !== null;

  const persist = useCallback((next: ConsentState) => {
    setState(next);
    writeStored(next);
    window.dispatchEvent(new CustomEvent('inferrex:consent-change', { detail: next }));
  }, []);

  const acceptAll = useCallback(() => {
    persist({ ...DEFAULT_DENIED, analytics: true, marketing: true, decidedAt: Date.now() });
    setIsOpen(false);
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({ ...DEFAULT_DENIED, decidedAt: Date.now() });
    setIsOpen(false);
  }, [persist]);

  const setCategory = useCallback<ConsentContextValue['setCategory']>(
    (category, granted) => {
      const base = state ?? { ...DEFAULT_DENIED, decidedAt: Date.now() };
      persist({ ...base, [category]: granted, decidedAt: Date.now() });
    },
    [state, persist],
  );

  const save = useCallback<ConsentContextValue['save']>(
    (partial) => {
      const base = state ?? { ...DEFAULT_DENIED, decidedAt: Date.now() };
      persist({ ...base, ...partial, decidedAt: Date.now() });
      setIsOpen(false);
    },
    [state, persist],
  );

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      hasDecided,
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      acceptAll,
      rejectAll,
      setCategory,
      save,
    }),
    [state, hasDecided, isOpen, acceptAll, rejectAll, setCategory, save],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used inside <ConsentProvider>');
  }
  return ctx;
}

/**
 * Subscribe to consent changes from outside React (e.g. analytics scripts
 * that need to react to consent updates without re-mounting).
 */
export function onConsentChange(handler: (state: ConsentState) => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (e: Event) => handler((e as CustomEvent<ConsentState>).detail);
  window.addEventListener('inferrex:consent-change', listener);
  return () => window.removeEventListener('inferrex:consent-change', listener);
}
